import React from 'react';
import { color, font, radius, tint } from '../tokens';
import Button from '../components/Button';
import Field from '../components/Field';
import Checkbox from '../components/Checkbox';
import ResearchChoice from '../components/ResearchChoice';
import { Body, Card, ScreenTitle } from '../components/Primitives';
import { useEnrollmentForm } from '../hooks';
import { CONSENT_TERMS, consentWording } from '../data/consentTerms';
import { firstName } from '../data/researchConsent';
import { FACILITY_WAIVER_FOOTNOTE, FACILITY_WAIVER_REQUIRED, isAdultOnDate } from '../data/signup';

// The tick moved to components/Checkbox.js; RegistrationFacility.js still imports it from here.
export { default as Checkbox } from '../components/Checkbox';

/**
 * Registration's consent step, its info sheet and the checkbox (Sprint 20,
 * spec 2.1 step 5) - split out of RegistrationSteps.js so that file stays
 * under 500 lines (the plan's cut plus the new athlete fields ran to 586).
 * RegistrationSteps.js re-exports ConsentStep and ConsentInfoSheet, so
 * Registration.js imports every step from one module as before. The
 * components themselves are the Sprint 10 code, with the adult copy variant:
 * `mode` 'athlete' (the 18+ self sign-up) reads every row and sheet in the
 * second person via consentWording (data/consentTerms.js).
 * `facilityRequired` (an athlete kept the facility add-on on the package
 * step, owner 2026-09-30) makes the optional facility waiver required: its
 * footnote says why, and the error says how to get past it either way.
 * Research (assessments spec 7) is its own opt-in, one per athlete, below
 * the consents: shown when the caller passes `onResearch` (sign-up), with
 * `athletes` and `todayISO` deciding who is under 18.
 */
export function ConsentStep({
  mode, consents, onChange, signatureName, onSignatureChange, onOpenInfo, showErrors, facilityRequired = false,
  athletes = [], todayISO, onResearch, onOpenResearch,
}) {
  const { data } = useEnrollmentForm();
  const list = data?.consents ?? [];

  const set = (id, v) => onChange((prev) => ({ ...prev, [id]: v }));
  const footnoteOf = (consent) => (facilityRequired && consent.id === 'facilityAccess' ? FACILITY_WAIVER_FOOTNOTE : consent.footnote);
  // A parent may add an 18+ athlete: then "Each athlete is a minor." is not true (review 2026-10-28).
  const anAdult = mode !== 'athlete' && athletes.some((a) => isAdultOnDate(a.dob, todayISO));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
      <Body size={13}>
        {mode === 'athlete'
          ? 'You are signing for yourself. Each of these is a separate decision — none is bundled into the others.'
          : `${anAdult ? '' : 'Each athlete is a minor. '}Each of these is a separate decision — none is bundled into the others.`}
      </Body>

      {/*
        Three cards, not three checkboxes in a row. Media release is optional and
        declining it does not block enrollment - bundling it with the injury
        waiver would weaken both.
      */}
      {list.map((consent) => (
        <Card key={consent.id} large>
          <div style={{ display: 'flex', gap: 13 }}>
            <Checkbox
              checked={consents[consent.id] ?? consent.checked}
              onChange={(v) => set(consent.id, v)}
              label={consent.title}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ font: `600 14px ${font.body}`, color: color.text }}>{consent.title}</div>
              <Body size={12} style={{ marginTop: 6 }}>
                {consentWording(consent.body, mode)}
              </Body>
              <button
                type="button"
                onClick={() => onOpenInfo(consent.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  marginTop: 9,
                  font: `500 12px ${font.body}`,
                  color: color.primary,
                  cursor: 'pointer',
                }}
              >
                {consent.link} →
              </button>
              {footnoteOf(consent) ? (
                <div
                  style={{
                    font: `500 10px ${font.body}`,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                    color: color.secondary,
                    marginTop: 9,
                  }}
                >
                  {footnoteOf(consent)}
                </div>
              ) : null}
            </div>
          </div>
        </Card>
      ))}

      {onResearch ? (
        <Card large>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {(mode === 'athlete' ? athletes.slice(0, 1) : athletes).map((a) => {
              const self = mode === 'athlete';
              const name = firstName(a.name);
              return (
                <ResearchChoice
                  key={a.key}
                  name={name}
                  self={self}
                  minor={!self && !isAdultOnDate(a.dob, todayISO)}
                  ticked={a.research === true}
                  assent={a.researchAssent === true}
                  onChange={({ ticked, assent }) => onResearch(a.key, { research: ticked, researchAssent: assent })}
                  onReadMore={onOpenResearch ? () => onOpenResearch({ name, self }) : undefined}
                />
              );
            })}
          </div>
        </Card>
      ) : null}

      {showErrors && !(consents.dataCollection && consents.videoCapture) ? (
        <div data-field-error>
          <Body size={12} tone={color.error}>
            Data collection and video capture consent are required to enroll.
          </Body>
        </div>
      ) : null}
      {showErrors && facilityRequired && !consents.facilityAccess ? (
        <div data-field-error>
          <Body size={12} tone={color.error}>{FACILITY_WAIVER_REQUIRED}</Body>
        </div>
      ) : null}

      <Card large>
        <Field
          label="Type your full legal name"
          value={signatureName}
          onChange={onSignatureChange}
          error={showErrors && signatureName.trim() === '' ? 'A signature is required.' : undefined}
        />
        {/* Says only what was shown and ticked: no injury waiver is in this
            flow, and media release may have been declined just above. */}
        <Body size={11} tone={color.textTertiary} style={{ marginTop: 9 }}>
          {mode === 'athlete'
            ? 'Typing your name signs the consents you ticked above. Re-confirmed each year.'
            : 'Typing your name signs the consents you ticked above, for every athlete listed. Data collection, video and media also cover any athlete you add later. Re-confirmed each year.'}
        </Body>
      </Card>
    </div>
  );
}

/**
 * In-app sheet for the consent "Read what is stored →" / "Read retention
 * policy →" / "Read media terms →" links (Sprint 10 pin A). Real copy from
 * docs, not a placeholder: each consent's own body is already the design
 * handoff's authoritative description of what is collected and why; the
 * data-collection sheet also carries the handoff's Access control matrix
 * (docs/portal/design-handoff.md, "Access control") verbatim, since that is
 * the closest documented "who can see what is stored" reference on file.
 * Never inert - a real sheet opens, or the link would lose its pointer
 * styling, which this does not.
 */
const CONSENT_INFO = {
  dataCollection: {
    title: 'What is stored',
    extra: (
      <>
        <Body size={12} style={{ marginTop: 12 }}>
          Access is role-scoped and re-checked on every request, never only in the UI:
        </Body>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {[
            ['Athlete', 'Own records, plus the shared session schedule and Tour results every member sees.'],
            ['Parent/Guardian', "Their own family's athletes, plus the shared session schedule and Tour results every member sees. No access to mental-game notes."],
            // Owner 2026-11-05: the row says what the rules really allow (any coach GETs any
            // athlete doc and reads every roster, Tour result and assessment; medical and
            // practice logs stay with the assigned coach; households and mental notes never).
            ['Coach', "Every athlete's profile (name, date of birth, handicap, package, billing status and payment-account ids, the athlete's own login email, and the family's consent answers and signature), session rosters and waitlists, Tour results, and physical assessment results, including the pain and injury check. Medical info and practice logs only for their own assigned athletes. Never the guardian's email, phone or address, or mental-game notes."],
            ['Mental Performance Coach', "Mental-game notes (no other role can read them), plus every athlete's profile, practice logs, medical info and family contact details."],
            ['Ops Admin', "Every athlete's profile, family contact details, billing status, enrollment and practice logs; enters assessments, Tour results and session notes. No medical info or mental-game notes."],
            ['Owner/Director', 'Everything above except mental-game notes, plus staff management.'],
          ].map(([role, access]) => (
            <div key={role}>
              <div style={{ font: `600 12px ${font.body}`, color: color.text }}>{role}</div>
              <div style={{ font: `400 11px/1.5 ${font.body}`, color: color.textTertiary }}>{access}</div>
            </div>
          ))}
        </div>
        <Body size={11} tone={color.textTertiary} style={{ marginTop: 12 }}>
          MFA is required on every staff role at setup.
        </Body>
      </>
    ),
  },
  videoCapture: { title: 'Retention policy', extra: null },
  mediaRelease: { title: 'Media terms', extra: null },
};

export function ConsentInfoSheet({ id, mode, onClose }) {
  const { data } = useEnrollmentForm();
  const consent = (data?.consents ?? []).find((c) => c.id === id);
  const info = CONSENT_INFO[id] || { title: 'What is stored', extra: null };

  return (
    <div
      data-portal-overlay=""
      onClick={onClose}
      style={{ position: 'absolute', inset: 0, background: tint.overlay, display: 'flex', alignItems: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxHeight: '80%',
          overflowY: 'auto',
          background: color.surface,
          borderTop: `1px solid ${color.border}`,
          borderRadius: `${radius.cardLarge} ${radius.cardLarge} 0 0`,
          padding: '20px 22px 26px',
        }}
      >
        <ScreenTitle size={19}>{info.title}</ScreenTitle>
        <Body size={12} style={{ marginTop: 10 }}>
          {consentWording(consent?.body, mode)}
        </Body>
        {info.extra}
        {/* The full terms (data/consentTerms.js), section by section. */}
        {(CONSENT_TERMS[id] || []).map((section) => (
          <div key={section.heading} style={{ marginTop: 14 }}>
            <div style={{ font: `600 12px ${font.body}`, color: color.text }}>{section.heading}</div>
            {section.lines.map((line) => (
              <Body key={line} size={12} style={{ marginTop: 6 }}>{consentWording(line, mode)}</Body>
            ))}
          </div>
        ))}
        <Button variant="outline" height={46} onClick={onClose} style={{ marginTop: 18, boxShadow: 'none' }}>
          Close
        </Button>
      </div>
    </div>
  );
}
